import { useEffect, useMemo, useState } from "react";

import { StorageKeys } from "@/storage/storageKeys";

const KEY = StorageKeys.shiftSelectedGroup;

/** migrate old keys once, if present */
function readInitial(): string {
	if (typeof window === "undefined") return "all";
	const oldKeys = ["shiftlog:selectedGroup", "shuftlog:selectedGroup"]; // typo included
	const current = window.localStorage.getItem(KEY);
	if (current) return current;

	for (const k of oldKeys) {
		const v = window.localStorage.getItem(k);
		if (v != null && v !== "") {
			// normalize legacy numeric/NaN to string
			const normalized = v === "all" ? "all" : String(v); // guard: if legacy stored number, store as string
			window.localStorage.setItem(KEY, normalized);
			return normalized;
		}
	}
	return "all";
}

export type SharedGroup = string; // "all" or "<id>"

export function useSharedSelectedGroup() {
	const [selectedGroup, setSelectedGroupState] = useState<SharedGroup>(() =>
		readInitial(),
	);

	// Persist + notify (storage event only fires in other tabs; that's fine)
	const setSelectedGroup = (next: SharedGroup) => {
		setSelectedGroupState(next);
		if (typeof window !== "undefined") {
			window.localStorage.setItem(KEY, next);
		}
	};

	// Cross-tab sync
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === KEY && e.newValue) {
				setSelectedGroupState(e.newValue);
			}
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	/** Convenience: convert to number for APIs (undefined when "all") */
	const groupIdOrUndefined = useMemo(() => {
		return selectedGroup === "all" ? undefined : Number(selectedGroup);
	}, [selectedGroup]);

	return {
		selectedGroup,
		setSelectedGroup,
		groupIdOrUndefined,
		storageKey: KEY,
	};
}
